// packages/web/src/lib/auth/token-manager.ts
// JWT token parsing, validation, and management

const UUID_RE = /^[0-9a-f-]{8}-[0-9a-f-]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isUuid = (v?: string | null): v is string => !!v && UUID_RE.test(v);

const safeAtob = (b64: string) => {
  try {
    // @ts-ignore atob in browsers
    return typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('utf8');
  } catch { return ''; }
};

// Convert JWT Base64URL -> Base64 (adds missing padding too)
function b64UrlToB64(seg: string): string {
  try {
    let s = String(seg || '').replace(/-/g, '+').replace(/_/g, '/');
    const pad = s.length % 4;
    if (pad) s += '='.repeat(4 - pad);
    return s;
  } catch { return ''; }
}

export function decodeTenantId(token: string | null): string | null {
  if (!isLikelyJwt(token)) return null;
  try {
    const payload = JSON.parse(safeAtob(b64UrlToB64((token as string).split('.')[1])));
    const tid = payload?.tenantId ?? payload?.tenant_id ?? null;
    return typeof tid === 'string' ? tid : null;
  } catch { return null; }
}

export function decodeSubdomainFromJwt(token: string | null): string | null {
  if (!isLikelyJwt(token)) return null;
  try {
    const payload = JSON.parse(safeAtob(b64UrlToB64((token as string).split('.')[1])));
    const sub = (payload?.subdomain ?? '').toString().trim();
    return sub || null;
  } catch { return null; }
}

export function isLikelyJwt(token?: string | null): token is string {
  return !!token && token.split('.').length === 3;
}

export function isStubToken(token?: string | null): token is string {
  return !!token && token.split('.').length === 3 && token.split('.')[2] === 'stub';
}

export function validateToken(token: string): { isValid: boolean; expiresAt?: Date; isExpired: boolean } {
  if (!isLikelyJwt(token)) {
    console.log('[TOKEN_MANAGER][validateToken] Token is not JWT-like');
    return { isValid: false, isExpired: false };
  }

  try {
    const payload = JSON.parse(safeAtob(b64UrlToB64(token.split('.')[1])));
    const exp = payload.exp;
    const now = Math.floor(Date.now() / 1000);
    const isExpired = exp && exp < now;

    console.log('[TOKEN_MANAGER][validateToken] Token validation:', {
      hasExp: !!exp,
      expTimestamp: exp,
      currentTimestamp: now,
      isExpired,
      expiresAt: exp ? new Date(exp * 1000).toISOString() : 'N/A',
      timeUntilExpiry: exp ? `${Math.floor((exp - now) / 60)} minutes` : 'N/A'
    });

    return {
      isValid: true,
      expiresAt: exp ? new Date(exp * 1000) : undefined,
      isExpired: !!isExpired
    };
  } catch (error) {
    console.error('[TOKEN_MANAGER][validateToken] Error parsing token:', error);
    return { isValid: false, isExpired: false };
  }
}

export function extractToken(resp: any): string | null {
  try {
    const d = resp?.data ?? {};
    if (d.token) return d.token;
    if (d.accessToken) return d.accessToken;
    if (d.jwt) return d.jwt;
    const auth = resp?.headers?.authorization || resp?.headers?.Authorization;
    if (auth && typeof auth === 'string' && auth.toLowerCase().startsWith('bearer ')) {
      return auth.slice(7);
    }
  } catch {}
  return null;
}